import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
} from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import {
  PaymentStore,
  FormConfigStore,
  ArticlesStore,
  MunicipalityStore,
  ArticleInfo,
  DeliveryInfoSummary,
  TypeCreateOrderFN,
  DateFormatter,
  NewPaymentAndDelivery,
  PaymentAndDelivery,
} from 'ewp-common';

import {
  Datalist,
  Button,
} from '@ktag/styleguide/dist/react';

import orderProcess from '../../../services/orderProcess';
import formDataStore from '../../../stores/formDataStore';

interface Props {
  id?: string;
  className?: string;
  additionalClassNames?: string;
  setRoute?: Dispatch<SetStateAction<number>>;
  createOrderFn: TypeCreateOrderFN
}

const Payment = (props: Props) => {
  const {
    id,
    className,
    additionalClassNames,
    setRoute,
    createOrderFn,
  } = props;

  const {
    state: articlesState,
    fetchArticles,
    totalAmount,
  } = useContext(ArticlesStore.ArticlesContext);

  const amountCurrency = articlesState?.articles?.data?.[0]?.currency_ISO;

  const {
    state: formConfigState,
  } = useContext(FormConfigStore.FormConfigContext);

  const {
    state: formDataState,
  } = useContext(formDataStore.FormDataContext);

  const {
    state: municipalityState,
  } = useContext(MunicipalityStore.MunicipalityContext);

  const {
    state: paymentState,
  } = useContext(PaymentStore.PaymentContext);

  const createOrder = async () => {
    const formDataValue = {
      ...formDataState,
      municipalityId: municipalityState.selectedMunicipality?.data?.gemeinde?.gemeinde_ID,
    };

    const reqData = {
      formData: formDataValue,
      amountData: {
        amount: articlesState.amount,
        currency: amountCurrency,
      },
      paymentMethod: paymentState.selectedPaymentMethod,
      paymentCollectionType: paymentState.paymentCollectionType,
      deliveryType: paymentState.deliveryType
    };
    createOrderFn(
      orderProcess.createOrder,
      reqData,
      formConfigState?.data?.labelsMap?.errorLabel,
      () => setRoute(4),
    );
  };

  const {
    personal,
    deliveryInfo,
  } = formDataState;

  const { search } = useLocation();
  const status = search && new URLSearchParams(search).get('status');

  const data = formConfigState?.data?.labelsMap;

  const {
    fieldAnrede,
    fieldName,
    fieldFirstname,
    fieldAhvNumber,
    fieldDob,
    fieldStrasse,
    fieldNr,
    fieldPlz,
    fieldOrt,
    fieldLand,
    fieldPhone,
    fieldEmail,
    fieldComments,
  } = personal;

  const municipalityId = municipalityState.selectedMunicipality?.data?.gemeinde?.gemeinde_ID;

  useEffect(() => {
    if (status) {
      setRoute(4);
    }
    if (municipalityId) {
      fetchArticles(municipalityId);
    }
    window.scrollTo(0, 0);
  }, []);

  let isDeliveryAddressSchweiz = true;

  if(deliveryInfo?.fieldDeliveryAddressDifferent != null && ((deliveryInfo?.fieldDeliveryAddressDifferent === 'Ja' && deliveryInfo?.fieldLand != 'Schweiz') || (deliveryInfo?.fieldDeliveryAddressDifferent === 'Nein' && fieldLand != 'Schweiz'))){
    isDeliveryAddressSchweiz = false;
  }

  return (
    <div id={id} className={classNames(className, additionalClassNames)}>
      <h2>{data.h2OrderOverview.labelDisplayName}</h2>
      <h3 className="mt-4">{data.h2Personalien.labelDisplayName}</h3>
      <Datalist
        items={[
          {
            key: data.fieldAnrede?.labelDisplayName,
            value: fieldAnrede,
          },
          {
            key: data.fieldName?.labelDisplayName,
            value: fieldName,
          },
          {
            key: data.fieldFirstname?.labelDisplayName,
            value: fieldFirstname,
          },
          {
            key: data.fieldAhvNumber?.labelDisplayName,
            value: fieldAhvNumber,
          },
          {
            key: data.fieldDob?.labelDisplayName,
            value: DateFormatter.formatDateForDisplay(fieldDob),
          },
          {
            key: data.fieldStrasseNr?.labelDisplayName,
            value: `${fieldStrasse} ${fieldNr}`,
          },
          {
            key: data.fieldPlzOrt?.labelDisplayName,
            value: `${fieldPlz} ${fieldOrt}`,
          },
          {
            key: data.fieldLand?.labelDisplayName,
            value: fieldLand,
          },
          {
            key: data.fieldPhone?.labelDisplayName,
            value: fieldPhone,
          },
          {
            key: data.fieldEmail?.labelDisplayName,
            value: fieldEmail,
          },
        ]}
      />

      <DeliveryInfoSummary
        formConfigs={data}
        deliveryInfoFormData={deliveryInfo}
      />

      <Datalist
        items={[
          {
            key: data.fieldComments?.labelDisplayName,
            value: fieldComments,
          },
        ]}
      />

      <ArticleInfo
        municipalityId={municipalityId}
        labelConfig={data}
        deliveryAddressCountry={deliveryInfo?.fieldDeliveryAddressDifferent != null && deliveryInfo?.fieldDeliveryAddressDifferent === 'Ja' ? deliveryInfo?.fieldLand : personal?.fieldLand}
        selectedDeliveryCode={paymentState.deliveryType}
        isPersonUnderage={false}
      />

      {process.env.REACT_APP_USE_NEW_PAYMENT_AND_DELIVERY_METHOD == "1" ?
      <NewPaymentAndDelivery
        municipalityId={municipalityId}
        labelConfig={data}
        showPaymentOption={true}
        showDeliveryOption={true}
        isDeliveryAddressSchweiz={isDeliveryAddressSchweiz}
      /> :
      <PaymentAndDelivery
        municipalityId={municipalityId}
        labelConfig={data}
        showPaymentOption={true}
        showDeliveryOption={true}
      />}

      <div className="payment-actions">
        <Button
          label={data.btnCancel?.labelDisplayName}
          onClick={() => {
            setRoute(2);
            window.location.reload;
          }}
        />
        <Button
          label={data.btnCompleteOrder?.labelDisplayName}
          onClick={createOrder}
          primary
          disabled={
            (!paymentState?.selectedDeliveryMethod)
            || (paymentState.paymentMethods?.data?.length > 0 && !paymentState.selectedPaymentMethod)
            || (paymentState.selectedPaymentMethod?.value?.toLowerCase() === '3' && !paymentState.paymentCollectionType)
            || (paymentState.selectedDeliveryMethod?.deliveryCode?.toLowerCase() === '3' && !paymentState.deliveryType)
          }
        />
      </div>
    </div>
  );
};

Payment.defaultProps = {
  id: '',
  className: 'payment',
  additionalClassNames: '',
};

export default Payment;
